import { useNavigate } from 'react-router-dom';
import { schoolApi } from '../api/endpoints.js';
import { useAuth } from '../state/AuthContext.jsx';
import { Icon, Wave } from '../components/Icons.jsx';
import { Button, Card, Grid2, Grid3, Muted, SectionTitle, Spinner, Tag, useAsync } from '../ui.jsx';

const steps = [
  ['Upload your results', 'Entrance reads your slip and checks it against real entrance rules and cut-offs.'],
  ['Build your profile once', 'Bio data, results and documents are saved to your account.'],
  ['Apply college by college', 'Each college has its own programmes, questions and application fee.'],
  ['Track and accept', 'Follow every school in one place. Accept one offer and print your letter.']
];

const faqs = [
  ['How does the advisor work?', 'Entrance checks your grades against each college\'s entrance rules and cut-offs using fixed rules, so the answer is the same every time. The advisor then explains what to improve and suggests courses that fit your grades and interests. It only advises. Each training college makes the final decision.'],
  ['Can I apply to several schools at the same time?', 'You apply to one school at a time. Finish and pay for one application, then start the next. Your master profile carries over, so you never type it twice.'],
  ['What is shared between schools?', 'Only your master profile: bio data, results and common documents. Programme choices, school questions and payment stay separate for each application.'],
  ['Is the aggregate final?', 'No. Entrance shows your aggregate and cut-offs as a guide. Each school makes its own decision.'],
  ['Which training colleges can I apply to through Entrance?', 'Every public and private College of Education in the directory is open for applications through Entrance.']
];

export default function Home() {
  const { user, isSchoolStaff } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error } = useAsync(() => schoolApi.list({ listing: 'ready' }), []);

  // The strip and totals come from the API, never from a hardcoded list, so the
  // landing page cannot drift from what the backend actually serves. This is
  // derived during render rather than stored, so it cannot fall out of step.
  const monograms = (data?.schools || []).map((s) => s.shortName);

  const primaryCta = isSchoolStaff
    ? { label: 'Open admin', to: '/admin' }
    : user
      ? { label: 'My applications', to: '/apply' }
      : { label: 'Apply', to: '/signup' };

  return (
    <>
      <section className="hero">
        <div>
          <h1>One profile. Every training college in Ghana.</h1>
          <p>
            Upload your results and Entrance checks them against real College of Education
            requirements, then recommends courses you can apply for. Then apply college by college.
          </p>
          <div className="row">
            <Button onClick={() => navigate(primaryCta.to)}>{primaryCta.label}</Button>
            <Button variant="o" onClick={() => navigate('/match')}>Check my aggregate</Button>
          </div>
          {!user ? (
            <p style={{ margin: '16px 0 0', fontSize: 14 }}>
              Already applying?{' '}
              <button type="button" className="lk" onClick={() => navigate('/signin')}>Log in</button>
            </p>
          ) : null}
          <div className="hcards">
            <button type="button" className="hpill" onClick={() => navigate('/match')}>
              <span className="hp-ic"><Icon name="match" size={15} /></span>
              Entrance Match
              <span className="hp-go">Open</span>
            </button>
            <button type="button" className="hpill" onClick={() => navigate('/apply')}>
              <span className="hp-ic"><Icon name="apply" size={15} /></span>
              Entrance Shared
              <span className="hp-go">Open</span>
            </button>
            <button type="button" className="hpill" onClick={() => navigate('/forums')}>
              <span className="hp-ic"><Icon name="forums" size={15} /></span>
              Entrance Forums
              <span className="hp-go">Open</span>
            </button>
          </div>
        </div>
        <div className="demo">
          <img className="hero-shot" src="/school-girl.jpg" alt="" />
          <div className="hero-list">
            <div className="row sp">
              <b>What Entrance checks</b>
              <Tag tone="ok">Same rules every time</Tag>
            </div>
            {[
              ['Core subjects', 'English, Mathematics, Science or Social Studies'],
              ['Three electives', 'Your best three count towards the aggregate'],
              ['Programme cut-offs', 'Per programme, and marked when they are an estimate'],
              ['Documents', 'The four each school asks for, reused for every application']
            ].map(([label, detail]) => (
              <div key={label} className="prog">
                <div style={{ flex: 1 }}>
                  <b>{label}</b>
                  <div className="mu">{detail}</div>
                </div>
              </div>
            ))}
            <div className="mu" style={{ borderTop: '1px solid var(--br)', paddingTop: 10 }}>
              Your profile is saved once. Each school application is submitted and paid on its own.
            </div>
          </div>
        </div>
        <Wave />
      </section>

      {loading ? <Spinner label="Loading institutions" /> : null}
      {error ? <div className="note">We could not load the institution list just now.</div> : null}

      {monograms.length ? (
        <>
          <div className="strip">
            {monograms.map((short) => (
              <i className="ub" key={short}>{short}</i>
            ))}
          </div>
          <p className="mu" style={{ textAlign: 'center', margin: '-14px 0 26px' }}>
            {data.schools.length} {data.schools.length === 1 ? 'training college is' : 'training colleges are'} open
            for applications through Entrance.{' '}
            <button type="button" className="lk" onClick={() => navigate('/schools')}>
              Browse the full directory
            </button>
          </p>
        </>
      ) : null}

      <SectionTitle>How Entrance works</SectionTitle>
      <Grid3 className="flow">
        {steps.map(([title, detail], i) => (
          <Card key={title}>
            <div className="n">{i + 1}</div>
            <b>{title}</b>
            <Muted>{detail}</Muted>
          </Card>
        ))}
      </Grid3>

      <SectionTitle>Why students use Entrance</SectionTitle>
      <Grid2>
        <Card>
          <h3 style={{ fontSize: 19, marginBottom: 8 }}>Without Entrance</h3>
          <ul className="req">
            <li>Retype the same bio data on every college portal</li>
            <li>Upload the same documents again and again</li>
            <li>Guess cut-offs from different websites</li>
            <li>Track every school in a different place</li>
          </ul>
        </Card>
        <Card style={{ borderColor: 'var(--g)' }}>
          <h3 style={{ fontSize: 19, marginBottom: 8, color: 'var(--g)' }}>With Entrance</h3>
          <ul className="req">
            <li>Bio data and documents saved once</li>
            <li>Check eligibility before you pay a fee</li>
            <li>Each college gets its own application and payment</li>
            <li>One dashboard for every offer</li>
          </ul>
        </Card>
      </Grid2>

      <SectionTitle>Questions</SectionTitle>
      {faqs.map(([q, a]) => (
        <details key={q}>
          <summary>{q}</summary>
          <p>{a}</p>
        </details>
      ))}

      <div className="cta">
        <div>
          <h2>Start with your grades</h2>
          <p>It takes two minutes. Create your profile when you are ready to apply.</p>
        </div>
        <div className="row">
          <Button onClick={() => navigate('/match')}>Check my aggregate</Button>
          <Button variant="o" onClick={() => navigate('/signin')}>College staff sign-in</Button>
        </div>
      </div>
    </>
  );
}
